import React, { useEffect, useState } from 'react';
import Popup from 'reactjs-popup';
import api from '../../api';
import { toast } from 'react-toastify';
import { Button, Input, Select } from '../Common';
import { batteryTypeOptions } from '../../constants/batteryTypes';
import { MAX_CAPACITY_KWH, MIN_CAPACITY_KWH } from '../../constants/batteryCapacity';
import { HELD_FALLBACK_MESSAGE, isHeldOnboarding, onboardErrorMessage } from './onboardGatewayMessages';

const priceAreas = [
    { value: '', label: 'Price Area', hidden: true },
    { value: 'SE1', label: 'SE1' },
    { value: 'SE2', label: 'SE2' },
    { value: 'SE3', label: 'SE3' },
    { value: 'SE4', label: 'SE4' },
];

const inverterBrands = [
    { value: '', label: 'Inverter Brand', hidden: true },
    { value: 'xolta', label: 'Xolta' },
    { value: 'sungrow', label: 'Sungrow' },
    { value: 'pixii_home', label: 'Pixii Home' },
    { value: 'pixii_power_shaper', label: 'Pixii Power Shaper' },
    { value: 'fronius', label: 'Fronius' },
    { value: 'goodwe', label: 'GoodWe' },
    { value: 'huawei', label: 'Huawei' },
    { value: 'sigenergy', label: 'Sigenergy' },
    { value: 'solis', label: 'Solis' },
    // In beta: the label says so, the value sent to the API stays the brand.
    { value: 'solinteg', label: 'Solinteg (BETA)' },
    { value: 'growatt', label: 'Growatt (BETA)' },
    { value: 'saj', label: 'SAJ' },
];

const TOAST_OPTIONS = {
    position: 'top-center',
    autoClose: 300,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
};

const OnboardGatewayModal = ({ facilities, open, setOpen, onSuccess }) => {
    const facilityOptions = [
        { value: '', label: 'Facility', hidden: true },
        ...facilities.map(facility => {
            const base = facility.address || facility.facility_id;
            // Append the customer + facility numbers so the operator can confirm which
            // customer/facility this is while onboarding (facility number is the real
            // differentiator between a customer's facilities).
            const ids = [facility.retailer_customer_id, facility.retailer_facility_id].filter(Boolean).join(' · ');
            const label = ids ? `${base} (${ids})` : base;
            return { value: facility.facility_id, label };
        }),
    ];

    const [facilityId, setFacilityId] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [priceArea, setPriceArea] = useState('');
    const [inverterBrand, setInverterBrand] = useState('');
    // Optional: for inverter-bundled setups we only detect the inverter, so the operator
    // picks the battery brand from `batteryTypeOptions` (stored as free text server-side).
    const [batteryManufacturer, setBatteryManufacturer] = useState('');
    // Optional NAMEPLATE (gross) capacity in kWh — the figure on the battery label.
    // Many inverter-bundled setups cannot report it, so the operator can supply it.
    const [batteryCapacityKwh, setBatteryCapacityKwh] = useState('');
    const [error, setError] = useState('');
    // Set when the API held the onboarding (202): the gateway has never connected, the
    // request is recorded server-side and completes on its own once it does. The form
    // is replaced by a notice the operator has to dismiss, so it cannot be mistaken
    // for a completed onboarding.
    const [held, setHeld] = useState(null);
    // The route does a registry round-trip plus a hold write, so a second click while
    // the first is in flight would hold the same serial twice.
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && facilities.length === 1) {
            setFacilityId(facilities[0].facility_id);
        }
    }, [open, facilities]);

    const resetForm = () => {
        setFacilityId('');
        setSerialNumber('');
        setPriceArea('');
        setInverterBrand('');
        setBatteryManufacturer('');
        setBatteryCapacityKwh('');
        setError('');
        setHeld(null);
    };

    const onboardGateway = async () => {
        if (submitting) {
            return;
        }
        setError('');
        if (!facilityId || !serialNumber || !priceArea || !inverterBrand) {
            setError('You must select a facility, enter a Saveeye ID, and select a price area and inverter brand!');
            return;
        }

        // Validated here as well as server-side: this number feeds FCR bid sizing, and a
        // rejection is far more useful next to the field than as a 400 after submitting.
        const capacityInput = batteryCapacityKwh.trim();
        let battery_capacity_kwh;
        if (capacityInput) {
            battery_capacity_kwh = Number(capacityInput);
            if (!Number.isFinite(battery_capacity_kwh) || battery_capacity_kwh < MIN_CAPACITY_KWH || battery_capacity_kwh > MAX_CAPACITY_KWH) {
                setError(`Battery capacity must be a number between ${MIN_CAPACITY_KWH} and ${MAX_CAPACITY_KWH} kWh.`);
                return;
            }
        }

        setSubmitting(true);
        try {
            const battery_manufacturer = batteryManufacturer.trim();
            const result = await api.onboard_gateway(facilityId, {
                serial_number: serialNumber.trim(),
                inverter_brand: inverterBrand,
                price_area: priceArea,
                // Only include when provided — both fields are optional on the backend.
                ...(battery_manufacturer ? { battery_manufacturer } : {}),
                ...(battery_capacity_kwh === undefined ? {} : { battery_capacity_kwh }),
            });
            if (isHeldOnboarding(result)) {
                setHeld(result.data);
                return;
            }
            toast.success('Gateway onboarded successfully', TOAST_OPTIONS);
            resetForm();
            onSuccess();
        } catch (err) {
            console.error(err);
            setError(onboardErrorMessage(err));
        } finally {
            // Not part of resetForm: that is not called on the error path, and the Popup
            // stays mounted with open=false, so a missed reset would leave the button
            // disabled on the next open.
            setSubmitting(false);
        }
    };

    // The held onboarding is recorded server-side, so dismissing the notice is the
    // same as a success for the parent: close and refresh.
    const dismissHeld = () => {
        resetForm();
        onSuccess();
    };

    const handleClose = () => {
        if (held) {
            dismissHeld();
            return;
        }
        resetForm();
        setOpen(false);
    };

    return (
        <Popup open={open} onClose={handleClose} modal className="modal" closeOnDocumentClick={!submitting}>
            <div>
                <button className="modal__close" onClick={handleClose} disabled={submitting}>
                    &#10005;
                </button>
                <h5 className="modal__header">Onboard Gateway Remotely</h5>
                {held ? (
                    <>
                        <div className="modal__content">
                            <p className="modal__notice-message">{held.message || HELD_FALLBACK_MESSAGE}</p>
                            <p className="modal__notice-message">
                                Serial number: <strong>{held.serial_number}</strong>
                            </p>
                        </div>
                        <div className="modal__actions">
                            <Button text="Close" onClick={dismissHeld} />
                        </div>
                    </>
                ) : (
                    <>
                        <div className="modal__content">
                            <Select
                                id="gatewayFacility"
                                value={facilityId}
                                onChange={e => setFacilityId(e.target.value)}
                                options={facilityOptions}
                                fullWidth
                            />
                            <Input
                                id="gatewaySerialNumber"
                                type="text"
                                placeholder="Saveeye ID"
                                value={serialNumber}
                                onChange={e => setSerialNumber(e.target.value)}
                                fullWidth
                            />
                            <Select
                                id="gatewayPriceArea"
                                value={priceArea}
                                onChange={e => setPriceArea(e.target.value)}
                                options={priceAreas}
                                fullWidth
                            />
                            <Select
                                id="gatewayInverterBrand"
                                value={inverterBrand}
                                onChange={e => setInverterBrand(e.target.value)}
                                options={inverterBrands}
                                fullWidth
                            />
                            <Select
                                id="gatewayBatteryType"
                                value={batteryManufacturer}
                                onChange={e => setBatteryManufacturer(e.target.value)}
                                options={batteryTypeOptions}
                                fullWidth
                            />
                            <Input
                                id="gatewayBatteryCapacity"
                                type="number"
                                placeholder="Battery capacity in kWh, from the label (optional)"
                                value={batteryCapacityKwh}
                                onChange={e => setBatteryCapacityKwh(e.target.value)}
                                fullWidth
                            />

                            {error && <p className="create-facility__error-message">{error}</p>}
                        </div>
                        <div className="modal__actions">
                            <Button text={submitting ? 'Onboarding…' : 'Onboard'} onClick={onboardGateway} disabled={submitting} />
                            <Button text="Cancel" onClick={handleClose} className="modal__cancel" disabled={submitting} />
                        </div>
                    </>
                )}
            </div>
        </Popup>
    );
};

export default OnboardGatewayModal;
